import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
}

/** Shared label/hint/error wiring so every input is labelled and describes its errors. */
function useFieldIds(hint: unknown, error: unknown) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return { id, hintId, errorId, describedBy };
}

function FieldShell({
  id,
  label,
  hint,
  hintId,
  error,
  errorId,
  children,
}: FieldProps & { id: string; hintId?: string; errorId?: string; children: ReactNode }) {
  return (
    <div className="hx-field">
      <label htmlFor={id}>{label}</label>
      {hint && (
        <span id={hintId} className="hx-field__hint">
          {hint}
        </span>
      )}
      {children}
      {error && (
        <span id={errorId} className="hx-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function TextField({
  label,
  hint,
  error,
  ...input
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const ids = useFieldIds(hint, error);
  return (
    <FieldShell {...ids} label={label} hint={hint} error={error}>
      <input
        id={ids.id}
        className="hx-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        {...input}
      />
    </FieldShell>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  ...input
}: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ids = useFieldIds(hint, error);
  return (
    <FieldShell {...ids} label={label} hint={hint} error={error}>
      <textarea
        id={ids.id}
        className="hx-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        {...input}
      />
    </FieldShell>
  );
}

export function SelectField({
  label,
  hint,
  error,
  options,
  ...select
}: FieldProps &
  SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) {
  const ids = useFieldIds(hint, error);
  return (
    <FieldShell {...ids} label={label} hint={hint} error={error}>
      <select
        id={ids.id}
        className="hx-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        {...select}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

/** An accessible on/off switch (role="switch"). The whole row is the target. */
export function Switch({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <button
      type="button"
      role="switch"
      className="hx-switch"
      aria-checked={checked}
      aria-describedby={description ? `${id}-d` : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span>
        <span className="hx-label">{label}</span>
        {description && (
          <span id={`${id}-d`} className="hx-field__hint" style={{ display: "block" }}>
            {description}
          </span>
        )}
      </span>
      <span className="hx-switch__track" aria-hidden="true">
        <span className="hx-switch__thumb" />
      </span>
    </button>
  );
}

/** A toggleable filter chip (aria-pressed). */
export function Chip({
  pressed,
  onToggle,
  children,
}: {
  pressed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="hx-chip" aria-pressed={pressed} onClick={onToggle}>
      {children}
    </button>
  );
}
